import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Chip, Link, Typography } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import { LatestProcessCriteriaDTOTalentStatus, ProcessResponseDTOStatus, TalentResponseDTO } from '@/api/generated';
import BaseCell from '@/components/DataGrid/BaseCell';
import TextCell from '@/components/DataGrid/TextCell';
import UserCell from '@/components/DataGrid/UserCell';
import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter';
import ProcessStatusIcon from '@/components/ProcessStatusIcon';
import TestSessionSummary from '@/containers/Process/TestSessionSummary';
import { toTalent } from '@/root/linker';

import TalentStatusDisplay from './TalentStatusDisplay';

const columnHelper = createColumnHelper<TalentResponseDTO>();

export const talentColumns = (t: TFunction) => {
  const columns = [
    columnHelper.display({
      header: t('layouts.talents.columns.name'),
      cell: ({ row }) => (
        <Link component={RouterLink} to={toTalent({ talentId: row.original.id! })} sx={{ textDecoration: 'none' }}>
          <UserCell avatarVariant="talent-xsmall" sx={{ minWidth: 308 }} {...row.original}>
            {row.original.type && (
              <Chip
                variant="tag"
                label={t(`filters.options.talentTypes.${row.original.type.toLowerCase()}`, { ns: 'filters' })}
              />
            )}
          </UserCell>
        </Link>
      )
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.status'),
      cell: ({ row }) => (
        <BaseCell>
          <TalentStatusDisplay
            value={row.original.latestProcess?.talentStatus || LatestProcessCriteriaDTOTalentStatus.GHOST}
          />
        </BaseCell>
      )
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.email'),
      cell: ({ row }) => <TextCell minWidth={200}>{row.original.email}</TextCell>
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.idNumber'),
      cell: ({ row }) => <TextCell>{row.original.idNumber}</TextCell>
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.test'),
      cell: ({ row }) => {
        const hiddenTestSessionsCount =
          row.original.testSessionsCount && row.original.testSessionsCount > 1 ? row.original.testSessionsCount - 1 : 0;

        return (
          <BaseCell>
            {row.original.latestTestSession && <TestSessionSummary summary={row.original.latestTestSession} />}
            {!hiddenTestSessionsCount && <HiddenItemCounter hiddenItemsCount={hiddenTestSessionsCount} isDisabled />}
          </BaseCell>
        );
      }
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.process'),
      cell: ({ row }) => {
        if (!row.original.latestProcess) return null;
        const process = row.original.latestProcess;
        const hiddenProcessesCount =
          row.original.processesCount && row.original.processesCount > 1 ? row.original.processesCount - 1 : 0;

        return (
          <BaseCell>
            {process && <ProcessStatusIcon status={process?.status as ProcessResponseDTOStatus} />}
            <Typography variant="body3">{process?.name}</Typography>
            {!!hiddenProcessesCount && <HiddenItemCounter hiddenItemsCount={hiddenProcessesCount} isDisabled />}
          </BaseCell>
        );
      }
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.createdBy'),
      cell: ({ row }) => {
        const createdBy = row.original.createdBy || row.original.latestProcess?.createdBy;

        return createdBy ? <UserCell {...createdBy} /> : '';
      }
    }),
    columnHelper.display({
      header: t('layouts.talents.columns.organization'),
      cell: ({ row }) => <TextCell minWidth={180}>{row.original.organization?.name}</TextCell>
    })
  ];

  return columns;
};
